import { useState } from 'react';
import { Loader2, ArrowRight, Calendar, User, DollarSign, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { trpc } from '@/lib/trpc';
import { formatCurrency } from '@/lib/utils';
import { showSuccessToast, showErrorToast } from '@/hooks/use-mutation-with-toast';
import { useAuthStore } from '@/stores/auth-store';

interface Quote {
  id: string;
  quoteNumber: string;
  totalAmount: number;
  currency: string;
  travelStartDate: string | Date | null;
  travelEndDate: string | Date | null;
  customer?: {
    firstName: string;
    lastName: string;
    email: string;
  } | null;
  _count?: {
    items: number;
  };
}

interface ConvertToBookingModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  quote: Quote;
  onSuccess?: (bookingId: string) => void;
}

export function ConvertToBookingModal({
  open,
  onOpenChange,
  quote,
  onSuccess,
}: ConvertToBookingModalProps) {
  const [isConverting, setIsConverting] = useState(false);
  const user = useAuthStore((state) => state.user);

  const convertMutation = trpc.quote.convertToBooking.useMutation({
    onSuccess: (booking) => {
      setIsConverting(false);
      showSuccessToast('Quote converted to booking successfully');
      onOpenChange(false);
      onSuccess?.(booking.id);
    },
    onError: (error) => {
      setIsConverting(false);
      showErrorToast(error.message || 'Failed to convert quote to booking');
    },
  });

  const handleConvert = () => {
    setIsConverting(true);
    convertMutation.mutate({ quoteId: quote.id, userId: user?.id });
  };

  const formatDate = (date: string | Date | null) => {
    if (!date) return 'Not set';
    return new Date(date).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRight className="h-5 w-5 text-primary" />
            Convert Quote to Booking
          </DialogTitle>
          <DialogDescription>
            This will create a new booking from quote {quote.quoteNumber}. This action cannot be undone.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Quote Summary Card */}
          <div className="rounded-lg border bg-muted/30 p-4 space-y-3">
            {/* Customer */}
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10">
                <User className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium">
                  {quote.customer?.firstName} {quote.customer?.lastName}
                </p>
                <p className="text-sm text-muted-foreground">
                  {quote.customer?.email}
                </p>
              </div>
            </div>

            {/* Travel Dates */}
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                <Calendar className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Travel Dates</p>
                <p className="font-medium">
                  {formatDate(quote.travelStartDate)} - {formatDate(quote.travelEndDate)}
                </p>
              </div>
            </div>

            {/* Items Count */}
            {quote._count && (
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100">
                  <Package className="h-5 w-5 text-purple-600" />
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Quote Items</p>
                  <p className="font-medium">{quote._count.items} item(s)</p>
                </div>
              </div>
            )}

            {/* Total Amount */}
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-green-100">
                <DollarSign className="h-5 w-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Amount</p>
                <p className="text-lg font-bold text-green-600">
                  {formatCurrency(quote.totalAmount, quote.currency)}
                </p>
              </div>
            </div>
          </div>

          {/* What happens info */}
          <div className="rounded-lg bg-blue-50 border border-blue-200 p-3 text-sm text-blue-700">
            <p className="font-medium mb-1">What happens next:</p>
            <ul className="list-disc list-inside space-y-1 text-blue-600">
              <li>A new booking will be created with all quote details</li>
              <li>The quote status will change to "Converted"</li>
              <li>You'll be redirected to the new booking page</li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isConverting}
          >
            Cancel
          </Button>
          <Button onClick={handleConvert} disabled={isConverting}>
            {isConverting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            <ArrowRight className="mr-2 h-4 w-4" />
            Convert to Booking
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
